<template>
  <section class="admin-page">
    <div class="admin-scroll">
      <header class="admin-header">
        <div>
          <span class="admin-label">文章管理</span>
          <h2>管理博客文章</h2>
          <p class="admin-desc">查看文章列表，处理草稿、编辑内容，或发布新的博客文章。</p>
        </div>
        <button class="admin-btn admin-btn-primary" type="button" @click="openCreateModal">
          <span class="admin-btn-icon">+</span>
          新建文章
        </button>
      </header>

      <section class="admin-stats">
        <article v-for="card in overviewCards" :key="card.label" class="admin-stat">
          <span class="admin-stat-label">{{ card.label }}</span>
          <strong class="admin-stat-value">{{ card.value }}</strong>
        </article>
      </section>

      <section class="admin-panel">
        <div class="admin-toolbar">
          <label class="admin-search">
            <svg class="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              v-model="searchKeyword"
              type="text"
              class="admin-search-input"
              placeholder="搜索标题或内容"
              @keyup.enter="currentPage = 1; fetchArticles()"
            />
          </label>
          <div class="admin-filters">
            <button
              v-for="filter in statusFilters"
              :key="filter.value"
              type="button"
              :class="['admin-filter-btn', { active: statusFilter === filter.value }]"
              @click="statusFilter = filter.value"
            >
              {{ filter.label }} ({{ filter.count }})
            </button>
          </div>
        </div>

        <div class="admin-list">
          <div v-if="loading" class="admin-loading">
            <div class="admin-spinner"></div>
            <span>加载中...</span>
          </div>
          <article
            v-for="article in filteredArticles"
            :key="article.id"
            class="admin-list-item"
          >
            <div class="admin-list-body">
              <div style="display: flex; align-items: center; gap: 16px;">
                <div v-if="article.coverImage" class="admin-list-cover">
                  <img :src="resolveMediaUrl(article.coverImage)" :alt="article.title" />
                </div>
                <div style="flex: 1;">
                  <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 4px;">
                    <h3 class="admin-list-title">{{ article.title }}</h3>
                    <span :class="['admin-status', statusTone(article.status)]">{{ article.status }}</span>
                  </div>
                  <p class="admin-list-excerpt">{{ article.excerpt }}</p>
                  <div class="admin-list-meta">
                    <span class="admin-meta-tag">{{ formatDate(article.createdAt) }}</span>
                    <span v-for="tag in article.tags" :key="`${article.id}-${tag.id}`" class="admin-meta-tag">{{ tag.name }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="admin-list-actions">
              <button class="admin-btn admin-btn-ghost" type="button" @click="editArticle(article)">编辑</button>
              <button v-if="article.status === '已发布'" class="admin-btn admin-btn-ghost" type="button" style="color: var(--warning);" @click="hideArticle(article)">隐藏</button>
              <button v-if="article.status === '隐藏'" class="admin-btn admin-btn-ghost" type="button" style="color: var(--success);" @click="publishHiddenArticle(article)">公开</button>
              <button class="admin-btn admin-btn-ghost" type="button" style="color: var(--danger);" @click="deleteArticle(article.id)">删除</button>
            </div>
          </article>

          <div v-if="filteredArticles.length === 0" class="admin-empty">
            <h3>暂无匹配文章</h3>
            <p>当前筛选条件下没有找到文章，可以调整关键词或直接新建一篇文章。</p>
            <button class="admin-btn admin-btn-primary" type="button" @click="openCreateModal">新建文章</button>
          </div>
        </div>

        <AdminPagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :on-page-change="goToPage"
        />
      </section>
    </div>

    <!-- Modal -->
    <div v-if="showCreateModal" class="admin-modal-overlay" @wheel.prevent>
      <div :class="['admin-modal', { 'admin-modal-visible': modalVisible }]" @wheel.stop>
        <div class="admin-modal-header">
          <h2>{{ isEditing ? '编辑文章' : '创建文章' }}</h2>
          <button class="admin-modal-close" type="button" aria-label="关闭" @click="closeCreateModal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="admin-modal-body">
          <div v-if="loadingArticle" class="admin-loading">
            <div class="admin-spinner"></div>
            <span>正在加载文章内容...</span>
          </div>
          <template v-else>
            <div class="admin-field">
              <label class="admin-field-label required" for="modal-title">文章标题</label>
              <input id="modal-title" v-model.trim="modalForm.title" class="admin-input admin-input-title" type="text" placeholder="请输入文章标题" />
            </div>

            <div class="admin-form-row">
              <div class="admin-field">
                <label class="admin-field-label">标签</label>
                <div class="tag-select-wrapper" @click.stop>
                  <div class="tag-select" @click="showTagDropdown = !showTagDropdown">
                    <div class="tag-selected-tags">
                      <span v-if="selectedTags.length === 0" class="tag-placeholder">选择标签</span>
                      <span v-for="tag in selectedTags" :key="tag.id" class="tag-item">
                        {{ tag.name }}
                        <button type="button" class="tag-remove" @click.stop="removeTag(tag.id)">×</button>
                      </span>
                    </div>
                    <svg class="tag-arrow" :class="{ 'tag-arrow-open': showTagDropdown }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                  <div v-if="showTagDropdown" class="tag-dropdown">
                    <input
                      v-model="tagSearchKeyword"
                      type="text"
                      class="tag-search"
                      placeholder="搜索标签..."
                      @click.stop
                    />
                    <div class="tag-list">
                      <div v-if="filteredTags.length === 0" class="tag-empty">暂无匹配标签</div>
                      <div
                        v-for="tag in filteredTags"
                        :key="tag.id"
                        :class="['tag-option', { 'tag-option-selected': isTagSelected(tag) }]"
                        @click="toggleTag(tag)"
                      >
                        <span class="tag-option-name">{{ tag.name }}</span>
                        <svg v-if="isTagSelected(tag)" class="tag-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="admin-field">
              <label class="admin-field-label">封面图</label>
              <div
                :class="['cover-upload', { 'cover-dragover': coverDragover, 'cover-loading': coverUploading }]"
                @click="triggerCoverUpload"
                @dragover.prevent="coverDragover = true"
                @dragleave.prevent="coverDragover = false"
                @drop.prevent="handleCoverDrop"
              >
                <input ref="coverInput" type="file" accept="image/*" style="display: none" @change="handleCoverChange" />
                <template v-if="coverUploading">
                  <div class="cover-spinner"></div>
                  <span class="cover-text">正在上传...</span>
                </template>
                <template v-else-if="modalForm.coverImage">
                  <img :src="resolveMediaUrl(modalForm.coverImage)" class="cover-preview" alt="封面预览" />
                  <button class="cover-remove" type="button" @click.stop="removeCover">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </template>
                <template v-else>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color: var(--text-muted);"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <span class="cover-text">点击或拖拽上传封面图</span>
                </template>
              </div>
            </div>

            <div class="admin-field">
              <label class="admin-field-label" for="modal-excerpt">摘要</label>
              <textarea id="modal-excerpt" v-model.trim="modalForm.excerpt" class="admin-textarea" rows="3" placeholder="留空将自动从正文截取第一句话作为摘要" />
            </div>

            <div class="admin-field">
              <label class="admin-field-label">正文内容</label>
              <ArticleEditor v-model="modalForm.content" />
            </div>
          </template>
        </div>

        <div class="admin-modal-footer">
          <button class="admin-btn admin-btn-secondary" type="button" @click="closeCreateModal">取消</button>
          <button class="admin-btn admin-btn-primary" type="button" :disabled="isSubmitting" @click="saveArticle">
            <span v-if="isSubmitting" class="admin-spinner"></span>
            {{ isSubmitting ? '保存中...' : '保存' }}
          </button>
          <button v-if="!isEditing || currentArticleRawStatus !== 1" class="admin-btn admin-btn-success" type="button" :disabled="isSubmitting" @click="publishArticle">发布</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import ArticleEditor from '../components/ArticleEditor.vue'
import { uploadImage } from '../services/articleService'
import { resolveMediaUrl } from '@/utils/media'
import { getAdminArticles, getAdminArticle, createArticle, updateArticle, deleteArticle as deleteArticleApi, getArticleStats } from '@/services/articleApiService'
import { getTags } from '@/services/tagService'
import { useNotify } from '@/composables/useNotify'
import AdminPagination from '../components/admin/AdminPagination.vue'

const { notify, confirmDialog } = useNotify()

const defaultContent = `# 新文章标题

在这里开始编写你的博客内容。
`

const toExcerpt = (content) => {
  const plainText = (content || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/[#>*_\-\n\r]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!plainText) return '暂无摘要'

  const sentenceEnd = plainText.match(/[。！？.!?\n]/)
  if (sentenceEnd) {
    return plainText.slice(0, sentenceEnd.index + 1).trim()
  }

  return plainText.length > 100 ? `${plainText.slice(0, 100)}...` : plainText
}

const articles = ref([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(1)
const totalArticles = ref(0)
const PER_PAGE = 10
const articleStats = ref({ total: 0, draft: 0, published: 0, hidden: 0 })

const fetchArticles = async () => {
  loading.value = true
  try {
    const keyword = searchKeyword.value.trim()
    const statusMap = { '已发布': '1', '草稿': '2', '隐藏': '3' }
    const status = statusFilter.value === 'all' ? '' : statusMap[statusFilter.value] || ''
    const res = await getAdminArticles(currentPage.value, PER_PAGE, keyword, status)
    const data = res.data
    articles.value = (data?.list || []).map(a => ({
      id: a.id,
      title: a.title,
      content: '',
      status: a.status === 1 ? '已发布' : a.status === 2 ? '草稿' : '隐藏',
      tags: (a.tags || []).map(t => ({ id: t.id, name: t.name })),
      createdAt: a.created_at,
      updatedAt: a.updated_at,
      excerpt: a.summary,
      coverImage: a.cover_image
    }))
    totalArticles.value = data?.total || 0
    totalPages.value = data?.total_page || Math.ceil(totalArticles.value / PER_PAGE) || 1
  } catch (e) {
    notify.error(e.message || '获取文章列表失败')
  } finally {
    loading.value = false
  }
}

const fetchArticleStats = async () => {
  try {
    const res = await getArticleStats()
    articleStats.value = res.data || { total: 0, draft: 0, published: 0, hidden: 0 }
  } catch (e) {
    notify.error(e.message || '获取文章统计失败')
  }
}

const fetchAllTags = async () => {
  try {
    const res = await getTags()
    allTags.value = res.data || []
  } catch (e) {
    notify.error(e.message || '获取标签列表失败')
  }
}

const filteredTags = computed(() => {
  const keyword = tagSearchKeyword.value.trim().toLowerCase()
  if (!keyword) return allTags.value
  return allTags.value.filter(tag => tag.name.toLowerCase().includes(keyword))
})

const selectedTags = computed(() => {
  return allTags.value.filter(tag => selectedTagIds.value.includes(tag.id))
})

const toggleTag = (tag) => {
  const index = selectedTagIds.value.indexOf(tag.id)
  if (index === -1) {
    selectedTagIds.value.push(tag.id)
  } else {
    selectedTagIds.value.splice(index, 1)
  }
}

const removeTag = (tagId) => {
  const index = selectedTagIds.value.indexOf(tagId)
  if (index !== -1) {
    selectedTagIds.value.splice(index, 1)
  }
}

const isTagSelected = (tag) => {
  return selectedTagIds.value.includes(tag.id)
}

const searchKeyword = ref('')
const statusFilter = ref('all')
const showCreateModal = ref(false)
const modalVisible = ref(false)
const isEditing = ref(false)
const currentArticleId = ref(null)
const currentArticleRawStatus = ref(0)
const isSubmitting = ref(false)
const loadingArticle = ref(false)
const coverInput = ref(null)
const coverDragover = ref(false)
const coverUploading = ref(false)

// 标签相关
const allTags = ref([])
const selectedTagIds = ref([])
const tagSearchKeyword = ref('')
const showTagDropdown = ref(false)

watch(showCreateModal, (visible) => {
  if (visible) {
    const bodyScrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (bodyScrollbarWidth > 0) {
      document.body.style.paddingRight = `${bodyScrollbarWidth}px`
    }
  } else {
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
})

const modalForm = reactive({
  title: '',
  content: defaultContent,
  status: '草稿',
  tags: '',
  coverImage: '',
  excerpt: ''
})

const statusFilters = computed(() => [
  { label: '全部', value: 'all', count: articleStats.value.total },
  { label: '已发布', value: '已发布', count: articleStats.value.published },
  { label: '草稿', value: '草稿', count: articleStats.value.draft },
  { label: '隐藏', value: '隐藏', count: articleStats.value.hidden }
])

const overviewCards = computed(() => [
  { label: '文章总数', value: `${articleStats.value.total}` },
  { label: '已发布', value: `${articleStats.value.published}` },
  { label: '草稿数', value: `${articleStats.value.draft}` },
  { label: '隐藏', value: `${articleStats.value.hidden}` }
])

// 搜索和筛选已在后端完成，直接使用 articles
const filteredArticles = computed(() => articles.value)

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchArticles()
}

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })

const triggerCoverUpload = () => { if (!coverUploading.value) coverInput.value?.click() }
const handleCoverChange = (e) => { const file = e.target.files?.[0]; if (file) uploadCoverFile(file) }
const handleCoverDrop = (e) => { coverDragover.value = false; const file = e.dataTransfer?.files?.[0]; if (file?.type.startsWith('image/')) uploadCoverFile(file) }

const uploadCoverFile = async (file) => {
  coverUploading.value = true
  try {
    modalForm.coverImage = await uploadImage(file)
  } catch (error) {
    notify.error(`封面图上传失败：${error.message}`)
  } finally {
    coverUploading.value = false
    if (coverInput.value) coverInput.value.value = ''
  }
}

const removeCover = () => { modalForm.coverImage = ''; if (coverInput.value) coverInput.value.value = '' }

const resetModalForm = () => {
  modalForm.title = ''
  modalForm.content = defaultContent
  modalForm.status = '草稿'
  modalForm.tags = ''
  modalForm.coverImage = ''
  modalForm.excerpt = ''
  selectedTagIds.value = []
  tagSearchKeyword.value = ''
  showTagDropdown.value = false
  if (coverInput.value) coverInput.value.value = ''
}

const openCreateModal = () => {
  isEditing.value = false
  currentArticleId.value = null
  currentArticleRawStatus.value = 0
  resetModalForm()
  showCreateModal.value = true
  requestAnimationFrame(() => { modalVisible.value = true })
}

const closeCreateModal = () => {
  modalVisible.value = false
  setTimeout(() => { showCreateModal.value = false }, 250)
}

const editArticle = async (article) => {
  isEditing.value = true
  currentArticleId.value = article.id
  loadingArticle.value = true
  showCreateModal.value = true
  requestAnimationFrame(() => { modalVisible.value = true })
  try {
    const res = await getAdminArticle(article.id)
    const detail = res.data
    if (detail) {
      modalForm.title = detail.title || article.title
      modalForm.content = detail.content || ''
      modalForm.status = detail.status === 1 ? '已发布' : detail.status === 2 ? '草稿' : '隐藏'
      currentArticleRawStatus.value = detail.status
      selectedTagIds.value = (detail.tags || []).map(t => t.id)
      modalForm.coverImage = detail.cover_image || ''
      modalForm.excerpt = detail.summary || ''
    } else {
      modalForm.title = article.title
      modalForm.content = ''
      modalForm.status = article.status
      currentArticleRawStatus.value = article.status === '已发布' ? 1 : 0
      selectedTagIds.value = (article.tags || []).map(t => t.id)
      modalForm.coverImage = article.coverImage || ''
      modalForm.excerpt = article.excerpt || ''
    }
  } catch (e) {
    notify.error(e.message || '获取文章详情失败')
    closeCreateModal()
  } finally {
    loadingArticle.value = false
  }
}

const deleteArticle = async (id) => {
  if (!await confirmDialog('确定要删除这篇文章吗？删除后将无法恢复。')) return
  try {
    await deleteArticleApi(id)
    await fetchArticles()
    await fetchArticleStats()
    notify.success('文章删除成功')
  } catch (e) {
    notify.error(`删除失败：${e.message}`)
  }
}

const hideArticle = async (article) => {
  if (!await confirmDialog('确定要隐藏这篇文章吗？')) return
  try {
    await updateArticle(article.id, { status: 3 })
    await fetchArticles()
    await fetchArticleStats()
    notify.success('文章已隐藏')
  } catch (e) {
    notify.error(`隐藏失败：${e.message}`)
  }
}

const publishHiddenArticle = async (article) => {
  if (!await confirmDialog('确定要公开这篇文章吗？')) return
  try {
    await updateArticle(article.id, { status: 1 })
    await fetchArticles()
    await fetchArticleStats()
    notify.success('文章已公开')
  } catch (e) {
    notify.error(`公开失败：${e.message}`)
  }
}

const saveArticle = async () => {
  if (!modalForm.title) { notify.warning('请输入文章标题。'); return }
  if (!modalForm.content.trim()) { notify.warning('正文内容不能为空。'); return }
  isSubmitting.value = true
  try {
    const payload = {
      title: modalForm.title,
      content: modalForm.content,
      summary: modalForm.excerpt || toExcerpt(modalForm.content),
      cover_image: modalForm.coverImage,
      status: isEditing.value ? currentArticleRawStatus.value : 2,
      tags_id: selectedTagIds.value
    }

    if (isEditing.value) {
      await updateArticle(currentArticleId.value, payload)
    } else {
      await createArticle(payload)
    }

    await fetchArticles()
    await fetchArticleStats()
    closeCreateModal()
    notify.success('文章保存成功')
  } catch (e) {
    notify.error(`保存失败：${e.message}`)
  } finally {
    isSubmitting.value = false
  }
}

const publishArticle = async () => {
  if (!modalForm.title) { notify.warning('请输入文章标题。'); return }
  if (!modalForm.content.trim()) { notify.warning('正文内容不能为空。'); return }
  isSubmitting.value = true
  try {
    const payload = {
      title: modalForm.title,
      content: modalForm.content,
      summary: modalForm.excerpt || toExcerpt(modalForm.content),
      cover_image: modalForm.coverImage,
      status: 1,
      tags_id: selectedTagIds.value
    }

    if (isEditing.value) {
      await updateArticle(currentArticleId.value, payload)
    } else {
      await createArticle(payload)
    }

    await fetchArticles()
    await fetchArticleStats()
    closeCreateModal()
    notify.success('文章发布成功')
  } catch (e) {
    notify.error(`发布失败：${e.message}`)
  } finally {
    isSubmitting.value = false
  }
}

const statusTone = (status) => {
  const map = { '已发布': 'admin-status-published', '草稿': 'admin-status-draft', '隐藏': 'admin-status-hidden' }
  return map[status] || 'admin-status-draft'
}

const handleClickOutside = (e) => {
  if (!e.target.closest('.tag-select-wrapper')) {
    showTagDropdown.value = false
  }
}

// 筛选变化时回到第一页
watch(statusFilter, () => {
  currentPage.value = 1
  fetchArticles()
})

onMounted(() => {
  fetchArticles()
  fetchArticleStats()
  fetchAllTags()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* ===== Loading ===== */
.admin-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: var(--text-subtle);
  font-size: 14px;
}

/* ===== Cover Upload ===== */
.cover-upload {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 280px;
  min-height: 130px;
  border: 1.5px dashed var(--line-strong);
  border-radius: var(--radius-xl);
  background: var(--bg-soft);
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.cover-upload:hover {
  border-color: rgba(192, 57, 43, 0.3);
  background: rgba(192, 57, 43, 0.02);
}

.cover-dragover {
  border-color: var(--accent);
  background: rgba(192, 57, 43, 0.04);
}

.cover-loading {
  pointer-events: none;
  opacity: 0.6;
}

.cover-spinner {
  width: 24px;
  height: 24px;
  border: 2.5px solid var(--line);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: admin-spin 0.8s linear infinite;
}

.cover-text {
  font-size: 13px;
  color: var(--text-subtle);
}

.cover-preview {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: calc(var(--radius-xl) - 2px);
}

.cover-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: var(--radius);
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
}

.cover-remove:hover {
  background: rgba(192, 57, 43, 0.85);
}

/* ===== Tag Select ===== */
.tag-select-wrapper {
  position: relative;
}

.tag-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  padding: 4px 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--bg-panel);
  cursor: pointer;
  transition: border-color 0.15s;
}

.tag-select:hover {
  border-color: var(--accent);
}

.tag-selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.tag-placeholder {
  color: var(--text-muted);
  font-size: 14px;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(192, 57, 43, 0.1);
  color: var(--accent);
  font-size: 12px;
}

.tag-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}

.tag-remove:hover {
  background: rgba(192, 57, 43, 0.2);
}

.tag-arrow {
  flex-shrink: 0;
  margin-left: 8px;
  color: var(--text-muted);
  transition: transform 0.2s;
}

.tag-arrow-open {
  transform: rotate(180deg);
}

.tag-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--bg-panel);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 100;
  overflow: hidden;
}

.tag-search {
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: transparent;
  font-size: 13px;
  outline: none;
}

.tag-search::placeholder {
  color: var(--text-muted);
}

.tag-list {
  max-height: 160px;
  overflow-y: auto;
}

.tag-empty {
  padding: 12px;
  text-align: center;
  color: var(--text-muted);
  font-size: 12px;
}

.tag-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.1s;
}

.tag-option:hover {
  background: var(--bg-soft);
}

.tag-option-selected {
  background: rgba(192, 57, 43, 0.05);
}

.tag-option-name {
  font-size: 13px;
}

.tag-check {
  color: var(--accent);
}

</style>
